import { useContext, useEffect, useRef } from 'react'
import ReactPlayer from 'react-player'
import { FPS } from 'site/creation-tools/constants'
import { useCreationDrawLoop } from 'site/creation-tools/hooks/use-creation-draw-loop'
import { CreationToolSourceFile } from 'site/creation-tools/types'
import CreationToolOutputType from 'site/creation-tools/types/output-type'
import styled from 'styled-components'
import { useCreationSourcePreviewUrl } from '../../../hooks/use-creation-source-preview-url'
import { CreationSourceEditorContext } from '../../source-editor/manager'
import { SourceEditorEdits } from '../../source-editor/types'

const Video = styled.div`
    background: #000;
    height: 0;
    position: relative;
    width: 100%;

    canvas {
        height: 100%;
        left: 0;
        position: absolute;
        top: 0;
        width: 100%;
    }
`

const Canvas = styled.canvas`
    height: 100%;
    position: absolute;
    margin-left: auto;
    margin-right: auto;
    left: 0;
    right: 0;
`

const Player = styled(ReactPlayer)`
    left: 0;
    position: absolute;
    top: 0;
`

type Props = {
    className?: string
    source: CreationToolSourceFile
    edits?: SourceEditorEdits
    isMuted: boolean
    isPlaying: boolean
    onTimeUpdate?: (currentTime: number) => void
}

const CreationSourcePlayerVideo = ({
    className,
    source,
    edits,
    isMuted = true,
    isPlaying = true,
    onTimeUpdate,
}: Props) => {
    const { outputType } = useContext(CreationSourceEditorContext)
    const { height, width } = source
    const canvasRef = useRef<HTMLCanvasElement | null>(null)
    const playerRef = useRef<any>(null)
    const previewUrl = useCreationSourcePreviewUrl(source.source)
    const drawCount = useCreationDrawLoop(FPS)

    const onProgress = () => {
        if (!playerRef.current) return

        const currentTime = playerRef.current.getCurrentTime()

        onTimeUpdate && onTimeUpdate(currentTime)

        if (edits?.trim && (currentTime < edits.trim.start || currentTime > edits.trim.end)) {
            playerRef.current.seekTo(edits.trim.start, 'seconds')
        }
    }

    const drawFramesForGifOutput = () => {
        if (!isPlaying || !canvasRef.current) return

        const canvas = canvasRef.current
        const context = canvas.getContext('2d')
        const startTime = edits?.trim?.start || 0
        const endTime = edits?.trim?.end || source.duration

        if (edits?.frames) {
            const totalFrames = edits.frames.map((f) => f.frameCount || 1).reduce((a, b) => a + b)
            const startFrame = Math.floor((startTime / source.duration) * totalFrames)
            const endFrame = Math.floor((endTime / source.duration) * totalFrames)
            const currentFrame = startFrame + (drawCount % (endFrame - startFrame))

            if (currentFrame) {
                const { canvas: frameCanvas } = edits.frames[currentFrame]
                canvas.width = frameCanvas.width
                canvas.height = frameCanvas.height
                context?.drawImage(frameCanvas, 0, 0)

                onTimeUpdate?.(((currentFrame + 1) / totalFrames) * source.duration)
            }
        }
    }

    const drawFramesForVideoOutput = () => {
        if (!canvasRef.current || !edits?.frames?.length) return

        const canvas = canvasRef.current
        const context = canvas.getContext('2d')
        const watermark = edits.frames[0].canvas

        canvas.width = width
        canvas.height = height
        context?.drawImage(watermark, edits.crop?.x || 0, edits.crop?.y || 0)
    }

    useEffect(() => {
        if (outputType === CreationToolOutputType.Gif || (edits?.frames && edits.frames.length > 1)) {
            // If output its GIF
            drawFramesForGifOutput()
        } else if (outputType === CreationToolOutputType.Video || (edits?.frames && edits.frames.length === 1)) {
            // If output its video
            drawFramesForVideoOutput()
        }
    }, [canvasRef, drawCount, isPlaying, onTimeUpdate])

    if (outputType === undefined && edits?.frames && edits.frames.length > 1) {
        return <Canvas ref={canvasRef} className={className} />
    }

    return (
        <Video className={className} style={{ paddingBottom: `${(height / width) * 100}%` }}>
            <Player
                {...{
                    ref: playerRef,
                    url: previewUrl,
                    playing: isPlaying,
                    muted: isMuted,
                    loop: true,
                    playsinline: true,
                    height: '100%',
                    width: '100%',
                    progressInterval: 50,
                    onProgress,
                }}
            />
            {edits?.frames?.length && <canvas ref={canvasRef} />}
        </Video>
    )
}

export default CreationSourcePlayerVideo
